import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@vnetwork/ui/primitives';
import { useAuth } from '@/auth/AuthProvider';
import { env } from '@/config/env';
import { startSsoLogin } from '@/lib/sso';
import { requestGoogleAuthCode } from '@/lib/google-signin';
import { exchangeGoogleCode } from '@/api/auth-flow';
import { resolveAuthError } from '@/api/auth-error';

/** Google "G" mark (official brand colors) for the social sign-in button. */
function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.46 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58z" />
    </svg>
  );
}

/**
 * "Continue with Google" — standalone social sign-in shared across the auth
 * screens (login + register). With a standalone Google client configured
 * (APP_GOOGLE_CLIENT_ID) it pops Google's consent and exchanges the returned auth
 * code for app tokens directly (bypassing Keycloak; session marked 'google' so it
 * refreshes via the local auth service). Otherwise it falls back to the
 * Google-via-Keycloak (VNETWORK-ID) SSO flow.
 */
export function GoogleSignInButton({
  redirectTo = '/',
  label = 'Continue with Google',
  onError,
}: {
  redirectTo?: string;
  label?: string;
  onError?: (message: string | null) => void;
}) {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [loading, setLoading] = useState(false);

  const loginWithGoogle = async () => {
    setLoading(true);
    onError?.(null);
    try {
      if (env.google.clientId) {
        const code = await requestGoogleAuthCode(env.google.clientId);
        const session = await exchangeGoogleCode({ code });
        if (session && setSession(session, 'google')) {
          navigate(redirectTo, { replace: true });
        } else {
          onError?.('Google sign-in failed. Please try again.');
          setLoading(false);
        }
      } else {
        await startSsoLogin(redirectTo, 'google'); // redirects to Keycloak
      }
    } catch (e) {
      onError?.(resolveAuthError(e, 'Google login failed. Please try again.'));
      setLoading(false);
    }
  };

  return (
    <Button
      type="button"
      variant="secondary"
      fullWidth
      loading={loading}
      onClick={loginWithGoogle}
      startIcon={<GoogleIcon />}
    >
      {label}
    </Button>
  );
}
